import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";

interface DynamicFieldsEditorProps {
  dynamicFields: Array<{ key: string; value: string }>;
  setDynamicFields: React.Dispatch<
    React.SetStateAction<Array<{ key: string; value: string }>>
  >;
}

export default function DynamicFieldsEditor({
  dynamicFields,
  setDynamicFields,
}: DynamicFieldsEditorProps) {
  const handleDynamicKeyUpdate = (index: number, newKey: string) => {
    setDynamicFields((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        key: newKey,
      };
      return updated;
    });
  };

  const handleDynamicValueUpdate = (index: number, newValue: string) => {
    setDynamicFields((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value: newValue,
      };
      return updated;
    });
  };

  const handleDynamicFieldRemove = (index: number) => {
    setDynamicFields((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      // Always ensure at least one empty row remains
      if (
        filtered.length === 0 ||
        filtered.every((field) => field.key.trim() !== "")
      ) {
        filtered.push({ key: "", value: "" });
      }
      return filtered;
    });
  };

  const handleAddDynamicField = () => {
    setDynamicFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const formatFieldName = (name: string) => {
    return name
      .replace(/\./g, " ")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderDynamicField = (
    field: { key: string; value: string },
    index: number
  ) => {
    // Check if this field has been "committed" (both key and value filled and add button clicked)
    const isCommittedField =
      field.key.trim() !== "" && index < dynamicFields.length - 1;

    if (isCommittedField) {
      // Show as standard field format for committed fields
      return (
        <div
          key={`dynamic-committed-${index}`}
          className="grid grid-cols-4 items-center gap-4"
        >
          <Label className="text-left">{formatFieldName(field.key)}</Label>
          <div className="col-span-3 flex gap-2">
            <Input
              value={field.value}
              onChange={(e) => handleDynamicValueUpdate(index, e.target.value)}
              className="flex-1 bg-surface-active border-surface-border"
              placeholder="Value"
            />
            <Button
              onClick={() => handleDynamicFieldRemove(index)}
              size="sm"
              variant="outline"
              className="bg-transparent border-surface-border hover:bg-surface-hover text-white px-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    } else {
      // Always show input format for the last row (new property input)
      return (
        <div
          key={`add-property-${index}`}
          className="grid grid-cols-4 items-center gap-4"
        >
          <Label className="text-right">Add Property</Label>
          <div className="col-span-3 flex gap-2">
            <Input
              value={field.key}
              onChange={(e) => handleDynamicKeyUpdate(index, e.target.value)}
              placeholder="Property Name"
              className="flex-1 bg-surface-active border-surface-border"
            />
            <Input
              value={field.value}
              onChange={(e) => handleDynamicValueUpdate(index, e.target.value)}
              placeholder="Value"
              className="flex-1 bg-surface-active border-surface-border"
            />
            <Button
              onClick={handleAddDynamicField}
              size="sm"
              className="bg-primary hover:bg-primary/90 px-2"
              disabled={!field.key.trim() || !field.value.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <>{dynamicFields.map((field, index) => renderDynamicField(field, index))}</>
  );
}